import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import useMediaQuery from 'hooks/useMediaQuery';

import { getFromLocalStorage } from 'lib/localStorage';
import { TOOLBAR_HOSTS } from 'lib/posthog';
import { parseToBool } from 'lib/string';

import LOCAL_STORAGE from 'constants/localStorage';

const AD_CLIENT = 'ca-pub-9700650747779397';

const isDevHost = () =>
  typeof window !== 'undefined' &&
  TOOLBAR_HOSTS.includes(window.location.hostname);

const AD_SLOTS = {
  SIDEBAR_300x250: '8833388776',
  LANDSCAPE_320x50: '9875007702',
  LANDSCAPE_728x90: '5229185683',
  BLOG_AFTER_TOC: '1756090905',
  BLOG_BEFORE_LAST_LINK: '3507890344',
  BLOG_SIDEBAR_TOP: '2935265030',
  BLOG_SIDEBAR_STICKY: '9059865856',
  BLOG_PRE_FOOTER: '8824896287',
};

const AD_SLOT_NAMES = Object.fromEntries(
  Object.entries(AD_SLOTS).map(([name, id]) => [id, name])
);

const isValidBreakpointValue = (value) => value !== null && value !== undefined;

/**
 * Resolves whether an ad slot should be active based on the current
 * viewport breakpoint and the configured visibility array.
 *
 * @param {boolean[]} breakpoints - Current breakpoint state [isSm, isMd, isLg, isXl]
 * @param {Array} showOnBreakpoints - Visibility config per breakpoint (true/false/null)
 * @returns {boolean}
 */
const resolveBreakpointActive = (breakpoints, showOnBreakpoints) => {
  if (showOnBreakpoints.length === 0) return true;

  const currBreakpointIndex = breakpoints.lastIndexOf(true);

  // SSR or first render before useMediaQuery resolves
  if (currBreakpointIndex === -1) return false;

  if (showOnBreakpoints.length === breakpoints.length) {
    const currBreakpointValue = showOnBreakpoints[currBreakpointIndex];
    if (currBreakpointValue !== null) return currBreakpointValue;
  }

  for (let index = showOnBreakpoints.length - 1; index >= 0; index--) {
    if (index > currBreakpointIndex) continue;
    if (index === 0) return showOnBreakpoints[0] ?? true;
    if (isValidBreakpointValue(showOnBreakpoints[index])) {
      return showOnBreakpoints[index];
    }
  }

  return true;
};

const AdSenseSlot = ({
  slotId,
  width,
  height,
  className,
  format,
  layout,
  autoHeight = false,
  showAdSlotOnBreakpoints = [],
  ...rest
}) => {
  const adRef = useRef(null);
  const pushedRef = useRef(false);
  const { isSm, isMd, isLg, isXl } = useMediaQuery();

  const isActive = resolveBreakpointActive(
    [isSm, isMd, isLg, isXl],
    showAdSlotOnBreakpoints
  );

  useEffect(() => {
    if (!isActive || pushedRef.current || !adRef.current) return;

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
      pushedRef.current = true;
    } catch (error) {
      import('@sentry/nextjs').then(({ captureException }) => {
        captureException(error);
      });
    }
  }, [isActive]);

  if (!isActive) return null;

  if (isDevHost()) {
    const slotName = AD_SLOT_NAMES[slotId] || slotId;
    const formatLabel =
      [format, layout].filter(Boolean).join(' / ') || 'horizontal';

    return (
      <div
        className={twMerge('adsbygoogle', className)}
        data-ad-slot={slotId}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: width || '100%',
          height,
          background: '#f0f4f8',
          border: '2px dashed #94a3b8',
          borderRadius: 4,
          color: '#475569',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 11,
          gap: 2,
          textAlign: 'center',
          padding: 8,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 13 }}>{slotName}</span>
        <span>
          slot: {slotId} | {formatLabel}
        </span>
      </div>
    );
  }

  const isPersonalized = parseToBool(
    getFromLocalStorage(LOCAL_STORAGE.IS_POLICY_NUMBER_3_ACCEPTED)
  );

  return (
    <ins
      ref={adRef}
      className={twMerge('adsbygoogle', className)}
      data-ad-client={AD_CLIENT}
      data-ad-format={format || (width ? undefined : 'horizontal')}
      data-ad-layout={layout}
      data-ad-personalization={isPersonalized ? 'on' : 'off'}
      data-ad-slot={slotId}
      style={{
        display: 'block',
        width: width || '100%',
        height: autoHeight ? undefined : height,
      }}
      {...rest}
    />
  );
};

AdSenseSlot.propTypes = {
  slotId: PropTypes.string.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  format: PropTypes.string,
  layout: PropTypes.string,
  autoHeight: PropTypes.bool,
  showAdSlotOnBreakpoints: PropTypes.array,
};

export { AD_SLOTS, resolveBreakpointActive };
export default AdSenseSlot;
